import type { Scorecard, AnalysisResults, Blueprint } from '@/types/analysis';
import { isNucliusBlueprint } from '@/types/analysis';
import { buildNucliusScorecardText } from '@/utils/scorecardExport';
import {
  isOriginalityExcluded,
  originalityExclusionNotice,
  originalityVolatilityValue,
} from '@/utils/originalityDisplay';
import {
  clearanceFromEchoScore,
  clearanceLabel,
  echoScoreDisplay,
  ECHO_THRESHOLD_CLEARED,
} from '@/config/indexThresholds';
import { formatScriptId } from '@/utils/formatIndexId';
import { computeAttentionCurve } from '@/utils/attentionCurve';

type ForensicPdfInput = {
  scorecard: Scorecard;
  scriptTitle: string;
  sessionId: string;
};

function metricRows(scorecard: Scorecard): { label: string; value: number | null; display?: string }[] {
  const v7 = scorecard.v7_scores ?? scorecard.v6_scores;
  const pct = (v: number | null | undefined) =>
    v == null || Number.isNaN(v) ? null : Math.round(v);
  const avg = (vals: (number | null)[]) => {
    const ok = vals.filter((x): x is number => x != null);
    return ok.length ? Math.round(ok.reduce((a, b) => a + b, 0) / ok.length) : null;
  };
  return [
    { label: 'Genre promise', value: pct(v7?.genre_promise_pct ?? v7?.thematic_stakes_pct) },
    {
      label: 'Attention resonance',
      value: avg([pct(v7?.scene_strength_pct), pct(v7?.story_beat_strength_pct), pct(v7?.concentration_pct)]),
    },
    {
      label: 'Structural integrity',
      value: avg([pct(v7?.story_spine_pct), pct(v7?.etb_structure_pct), pct(v7?.pacing_pct)]),
    },
    { label: 'Commercial signal', value: pct(v7?.commercial_viability_pct ?? v7?.target_market_pct) },
    {
      label: 'Narrative volatility',
      value: originalityVolatilityValue(scorecard),
      display: isOriginalityExcluded(scorecard) ? 'not applicable' : undefined,
    },
  ];
}

/** Client-side forensic PDF via print dialog (no extra deps). */
export function exportForensicScorecardPdf(input: ForensicPdfInput): void {
  const { scorecard, scriptTitle, sessionId } = input;
  const scriptId = formatScriptId(sessionId);
  const display = echoScoreDisplay(scorecard.overall_engagement_score);
  const clearance = clearanceFromEchoScore(scorecard.overall_engagement_score);
  const delta =
    display != null ? (display - ECHO_THRESHOLD_CLEARED / 10).toFixed(1) : null;
  const metrics = metricRows(scorecard);
  const originalityNotice = originalityExclusionNotice(scorecard);
  const curve = computeAttentionCurve(scorecard);
  const now = new Date().toLocaleString('en-GB');

  const metricHtml = metrics
    .map(
      (m) => `
      <div class="metric">
        <div class="metric-head"><span>${m.label}</span><span>${m.display ?? (m.value != null ? `${m.value}%` : 'N/A')}</span></div>
        <div class="bar"><div class="fill" style="width:${m.value ?? 0}%"></div></div>
      </div>`,
    )
    .join('');

  const curveHtml =
    curve && curve.length > 1
      ? `<p class="mono">Attention series: ${curve.map((v) => v.toFixed(0)).join(' · ')}</p>`
      : '';

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><title>Echo Index — ${scriptTitle}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Inter, system-ui, sans-serif; background: #0a0a0a; color: #fff; padding: 40px; max-width: 720px; margin: 0 auto; }
  .label { font-size: 10px; letter-spacing: 0.25em; text-transform: uppercase; color: #888; }
  h1 { font-size: 28px; margin: 8px 0; }
  .score { font-size: 48px; font-weight: 700; text-align: right; }
  .mono { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #aaa; }
  .panel { border: 1px solid #2a2a2a; background: #141414; padding: 20px; margin-top: 24px; }
  .metric { margin-bottom: 12px; }
  .metric-head { display: flex; justify-content: space-between; font-size: 10px; text-transform: uppercase; color: #888; margin-bottom: 4px; }
  .bar { height: 6px; background: #1f1f1f; }
  .fill { height: 100%; background: #4ade80; }
  .clearance { color: #4ade80; font-size: 11px; text-transform: uppercase; letter-spacing: 0.15em; }
  footer { margin-top: 40px; font-size: 10px; color: #555; text-transform: uppercase; letter-spacing: 0.2em; }
  @media print { body { background: #fff; color: #000; } .panel { border-color: #ccc; } }
</style></head><body>
  <p class="label">Nuclius Index · Forensic scorecard</p>
  <p class="mono">${scriptId} · ${scriptTitle}</p>
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-top:16px">
    <div>
      <p class="label">Echo Index™</p>
      <p class="clearance">${clearanceLabel(clearance)}</p>
      ${delta != null ? `<p class="mono">${Number(delta) >= 0 ? '▲' : '▼'} ${Math.abs(Number(delta))} vs threshold</p>` : ''}
    </div>
    <div class="score">${display != null ? display.toFixed(1) : '—'}</div>
  </div>
  <div class="panel">${originalityNotice ? `<p class="mono" style="margin-bottom:16px;color:#f59e0b;white-space:pre-line">${originalityNotice}</p>` : ''}${metricHtml}${curveHtml}</div>
  <footer>Generated by Nuclius Index · ${now}</footer>
</body></html>`;

  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) {
    alert('Allow pop-ups to export the forensic PDF, then use Print → Save as PDF.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 400);
}

type NucliusPdfInput = {
  results: AnalysisResults;
  scriptTitle: string;
  sessionId: string;
};

/** Client-side Nuclius five-section PDF via print dialog. */
export function exportNucliusScorecardPdf(input: NucliusPdfInput): void {
  const { results, scriptTitle, sessionId } = input;
  const bp = results.blueprint as Blueprint | undefined;
  if (!isNucliusBlueprint(bp)) {
    return;
  }
  const label =
    bp?.commission_summary ??
    bp?.sections_structured?.recommendation?.recommendation_label ??
    '—';
  const scriptId = formatScriptId(sessionId);
  const now = new Date().toLocaleString('en-GB');
  const bodyText = buildNucliusScorecardText(results)
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^#+\s*/gm, '');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><title>Nuclius Index — ${scriptTitle}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Inter, system-ui, sans-serif; background: #0a0a0a; color: #fff; padding: 40px; max-width: 720px; margin: 0 auto; }
  .label { font-size: 10px; letter-spacing: 0.25em; text-transform: uppercase; color: #888; }
  h1 { font-size: 28px; margin: 8px 0; }
  .badge { display: inline-block; margin-top: 12px; padding: 8px 16px; border: 1px solid #4ade80; color: #4ade80; font-size: 14px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .mono { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #aaa; white-space: pre-wrap; line-height: 1.5; }
  .panel { border: 1px solid #2a2a2a; background: #141414; padding: 20px; margin-top: 24px; }
  footer { margin-top: 40px; font-size: 10px; color: #555; text-transform: uppercase; letter-spacing: 0.2em; }
  @media print { body { background: #fff; color: #000; } .panel { border-color: #ccc; } .badge { color: #166534; border-color: #166534; } }
</style></head><body>
  <p class="label">Nuclius Index · Scorecard</p>
  <p class="mono">${scriptId} · ${scriptTitle}</p>
  <h1>${scriptTitle}</h1>
  <p class="label">Final commissioning recommendation</p>
  <p class="badge">${label}</p>
  <div class="panel"><pre class="mono">${bodyText.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre></div>
  <footer>Generated by Nuclius Index · ${now}</footer>
</body></html>`;

  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) {
    alert('Allow pop-ups to export the scorecard PDF, then use Print → Save as PDF.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 400);
}
